import React, {FC, useRef} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {useLocalize} from '../../../../../core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../core/src/helpers/internationalization/message';
import {FrontKeyboardEvent} from '../../../../../core/src/helpers/react/reactHelpers';
import {Icon} from '../../icon/icon';
import {Input, InputTypesEnum} from '../../inputs/input';
import {useKeyboardNavigation} from '../../keyboardNavigation/keyboardNavigation';
import {typographyMixin} from '../../typography/typography';

/*
 * Props.
 */

interface RedesignedSearchInputContainerProps {
  inputId: string;
  placeholder?: string;
  onChange?: (arg: string) => void;
  value: string;
  onSearchInputEnter?: (e: FrontKeyboardEvent, query: string) => void;
}

/*
 * Styles.
 */

const InputContainer = styled.div`
  padding: 12px 15px;
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const StyledInput = styled(Input)`
  line-height: 18px;
  border: none;
  outline: none;
  appearance: none;
  background: none;
  width: 100%;

  &::placeholder {
    ${typographyMixin({color: 'disabled'})}
  }

  &:focus::placeholder {
    ${typographyMixin({color: 'tertiary'})}
  }
`;

/*
 * Component.
 */

export const RedesignedSearchInputContainer: FC<RedesignedSearchInputContainerProps> = ({
  inputId,
  placeholder,
  onChange,
  value,
  onSearchInputEnter,
}) => {
  const localize = useLocalize();
  const inputRef = useRef<HTMLInputElement>(null);

  useKeyboardNavigation({ref: inputRef});

  return (
    <InputContainer>
      <Icon name="search-sm/outlined/16" color="tertiary" />
      <StyledInput
        inputId={inputId}
        inputRef={inputRef}
        value={value}
        onChange={onChange}
        placeholder={
          placeholder ??
          localize(
            defineMessage({
              id: 'dropdown-search-placeholder',
              message: 'Search',
            }),
          )
        }
        type={InputTypesEnum.TEXT}
        onKeyDown={(e) => {
          if (!onSearchInputEnter || e.key !== Key.Enter) {
            return;
          }

          onSearchInputEnter(e, inputRef.current?.value ?? '');
        }}
        shouldFocus
      />
    </InputContainer>
  );
};
